import { UserAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getErrorMessage } from "../utils/errorMessages";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../translations";
import "../styles/auth.css";

const Signup = () => {
  const { googleSignIn, emailSignUp, user, loading } = UserAuth();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const T = translations[lang].signup;
  const Tg = translations[lang];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const handleGoogleSignIn = async () => {
    try {
      setError("");
      await googleSignIn();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError(T.errorName);
      return;
    }

    if (!role) {
      setError(T.errorRole);
      return;
    }

    if (password !== confirmPassword) {
      setError(T.errorPasswordMatch);
      return;
    }

    if (password.length < 6) {
      setError(T.errorPasswordLength);
      return;
    }

    try {
      setError("");
      await emailSignUp(email, password, name.trim(), role);
      setMessage(T.successMessage);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (user) {
      const isEmailPasswordUser = user.providerData &&
        user.providerData.some(provider => provider.providerId === 'password');

      if (isEmailPasswordUser && !user.emailVerified) {
        navigate("/verify-email");
      } else if (!isEmailPasswordUser || user.emailVerified) {
        navigate("/");
      }
    }
  }, [user, navigate]);

  if (loading) {
    return <div className="loading">{Tg.loading}</div>;
  }

  return (
    <div className="auth-container">
      <h1 className="auth-title">{T.title}</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="google-signin">
        <button className="gsi-material-button" onClick={handleGoogleSignIn} type="button">
          <div className="gsi-material-button-state"></div>
          <div className="gsi-material-button-content-wrapper">
            <div className="gsi-material-button-icon">
              <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlnsXlink="http://www.w3.org/1999/xlink" style={{display: 'block'}}>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="gsi-material-button-contents">{T.continueWithGoogle}</span>
            <span style={{display: 'none'}}>{T.continueWithGoogle}</span>
          </div>
        </button>
      </div>

      <div className="divider">
        <span>{T.or}</span>
      </div>

      <form onSubmit={handleEmailSignUp} className="auth-form">
        <div className="form-group">
          <label className="form-label">{T.nameLabel}</label>
          <input
            type="text"
            placeholder={T.namePlaceholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">{T.emailLabel}</label>
          <input
            type="email"
            placeholder={T.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">{T.passwordLabel}</label>
          <input
            type="password"
            placeholder={T.passwordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">{T.confirmPasswordLabel}</label>
          <input
            type="password"
            placeholder={T.confirmPasswordPlaceholder}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">{T.roleLabel}</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            required
            className="form-select"
          >
            <option value="">{T.roleSelect}</option>
            <option value="teacher">{T.roleEducator}</option>
            <option value="student">{T.roleStudent}</option>
            <option value="other">{T.roleOther}</option>
          </select>
        </div>

        <button type="submit" className="btn btn-primary">
          {T.signUpWithEmail}
        </button>
      </form>

      <div className="auth-links">
        <span>{T.alreadyHaveAccount} </span>
        <Link to="/signin">{T.signIn}</Link>
      </div>
      <div className="auth-links">
        <p>{T.googleNote}<br /> {T.googleNoteEmail}</p>
      </div>
    </div>
  );
};

export default Signup;
